import { useState, useEffect } from 'react';
import styled from "styled-components";
import {
    GridColDef,
    GridExceljsProcessInput,
    GridInitialState,
    GridRenderCellParams,
    GridState,
    GridLinkOperator,
    useGridApiContext,
    GridExportMenuItemProps,
    GridPreferencePanelParams,
    GridExcelExportOptions,
    GridToolbarExportContainer,
    GridToolbarContainer,
    GridToolbarQuickFilter,
    GridToolbarFilterButton,
    GridToolbarColumnsButton,
    GridToolbarContainerProps
} from "@mui/x-data-grid-premium";
import MenuItem from '@mui/material/MenuItem';
import DataGrid from "components/DataLists/DataGrid";
import { PageHeaderTitle } from "components/DesignUIComponents";
import DatePicker from 'components/DatePickers/DatePicker';
import EmployeeCard from "components/Employee/Card";
import useQuery from "hooks/useQueryCustom";
import { isEmpty, uniqBy } from "lodash";
import format from "date-fns/format";
import { formatInTimeZone } from 'date-fns-tz'
import { useSelector } from "react-redux";
import { currentUserSelector } from "redux/selectors";
import PermissionGate from 'permissions/PermissionGate';

import { ReactComponent as MagnifierIcon } from 'assets/svg/magnifier.svg';
import { ReactComponent as CloseIcon } from 'assets/svg/close-icon.svg';

interface ICustomToolbarProps extends GridToolbarContainerProps {
    setPanelsButtonEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>,
    date: Date,
    setDate: React.Dispatch<React.SetStateAction<any | null>>,
    openedPanelValue?: string | undefined
};

const renderAvatarCell = (params: GridRenderCellParams, type: string) => {
    return params.row[type]?.first_name ?
        <PermissionGate on='employee' properties={{ disabled: true }}>
            <EmployeeCard fontSize={14} employee={params.row[type]} />
        </PermissionGate> : ""
};

const columns: GridColDef[] = [
    {
        field: 'employee', headerName: 'Employee',
        renderCell: (params) => renderAvatarCell(params, 'employee'),
        valueGetter: ({ value }) => `${value.first_name} ${value.last_name}`,
        minWidth: 150, flex: 1
    },
    { field: 'employee_id', headerName: 'Emp. #', minWidth: 100, flex: 0.6 },
    {
        field: 'status', headerName: 'Status',
        type: 'singleSelect',
        valueOptions: ['Hiring', 'Active', 'Terminated'],
        minWidth: 100, flex: 0.6
    },
    {
        field: 'hire_date',
        headerName: 'Hire Date',
        type: 'date',
        valueGetter: ({ value }) => value && new Date(value),
        valueFormatter: ({ value }) => value && formatInTimeZone(new Date(value), 'UTC', 'P'),
        minWidth: 100, flex: 0.6
    },
    {
        field: 'termination_date',
        headerName: 'Termination Date',
        type: 'date',
        valueGetter: ({ value }) => value && new Date(value),
        valueFormatter: ({ value }) => value && formatInTimeZone(new Date(value), 'UTC', 'P'),
        minWidth: 100, flex: 0.6
    },
    { field: 'job_title', headerName: 'Job Title', minWidth: 100, flex: 1 },
    { field: 'employment_status', headerName: 'Emp. Status', minWidth: 100, flex: 1 },
    { field: 'department', headerName: 'Department', minWidth: 100, flex: 1 },
    { field: 'division', headerName: 'Division', minWidth: 100, flex: 1 },
    { field: 'location', headerName: 'Location', minWidth: 100, flex: 1 },
    { field: 'previous_rate', headerName: 'Previous Pay', minWidth: 100, flex: 1 },
    { field: 'prev_currency', headerName: 'Previous Pay (Currency)', minWidth: 100, flex: 1 },
    {
        field: 'prev_amount',
        headerName: 'Previous Pay (Amount)',
        type: 'number',
        valueGetter: ({ value }) => Number(value).toFixed(2),
        minWidth: 100, flex: 1,
    },
    { field: 'prev_payment_period', headerName: 'Previous Pay (Period)', minWidth: 100, flex: 1 },
    { field: 'prev_payment_type', headerName: 'Previous Pay Type', minWidth: 100, flex: 1 },
    { field: 'prev_payment_schedule', headerName: 'Previous Pay Schedule', minWidth: 100, flex: 1 },
    { field: 'prev_overtime', headerName: 'Previous Overtime', minWidth: 100, flex: 1 },
    { field: 'current_rate', headerName: 'Current Pay', minWidth: 100, flex: 1 },
    { field: 'current_currency', headerName: 'Current Pay (Currency)', minWidth: 100, flex: 1 },
    {
        field: 'current_amount',
        headerName: 'Current Pay (Amount)',
        type: 'number',
        valueGetter: ({ value }) => Number(value).toFixed(2),
        minWidth: 100, flex: 1,
    },
    { field: 'current_payment_period', headerName: 'Current Pay (Period)', minWidth: 100, flex: 1 },
    { field: 'current_payment_type', headerName: 'Current Pay Type', minWidth: 100, flex: 1 },
    { field: 'current_payment_schedule', headerName: 'Current Pay Schedule', minWidth: 100, flex: 1 },
    {
        field: 'change_date',
        headerName: 'Date of Change',
        type: 'date',
        valueGetter: ({ value }) => value && new Date(value),
        valueFormatter: ({ value }) => value && formatInTimeZone(new Date(value), 'UTC', 'P'),
        minWidth: 100, flex: 0.6
    },
    { field: 'change_reason', headerName: 'Reason of Change', minWidth: 100, flex: 1 },
];

const CustomToolbar = ({ setPanelsButtonEl, date, setDate, openedPanelValue, ...rest }: ICustomToolbarProps) => {
    const currentUser = useSelector(currentUserSelector);

    const [isFilterSelect, setIsFilterSelected] = useState<boolean>(false);
    const apiRef = useGridApiContext();
    useEffect(() => {
        return apiRef.current.subscribeEvent('stateChange', (
            ({ filter: { filterModel: { items } } }) => setIsFilterSelected(!isEmpty(items))
        ))
    }, [apiRef]);

    const exceljsPreProcess = ({ workbook, worksheet }: GridExceljsProcessInput): any => {
        workbook.creator = 'Profesto';
        workbook.created = new Date();
        worksheet.properties.defaultRowHeight = 30;

        worksheet.getCell('A1').value = 'Pay Change';
        worksheet.getCell('A1').font = {
            name: 'Arial Black',
            bold: true,
            size: 12,
        };
        worksheet.getCell('A1').alignment = {
            vertical: 'top',
            horizontal: 'left',
            wrapText: true,
        };
        worksheet.getCell('A2').value = currentUser.company.name;
        worksheet.getCell('A2').font = {
            name: 'Arial',
            size: 10,
        };
        worksheet.getCell('A3').value = format(new Date(), "MM.dd.yyyy HH:mm:ss");
        worksheet.getCell('A3').font = {
            name: 'Arial',
            size: 10,
        };
        worksheet.addRow({});
    };

    const exceljsPostProcess = ({ worksheet }: GridExceljsProcessInput): any => {
        worksheet.getRow(5).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'cccccc' }
        };
        worksheet.getRow(5).font = {
            size: 12,
            bold: true
        };
        worksheet.getRow(5).alignment = {
            vertical: 'middle',
            horizontal: 'center',
        };
        try {
            worksheet.getColumn('prev_amount').alignment = {
                horizontal: 'right',
            };
        } catch { }
        try {
            worksheet.getColumn('current_amount').alignment = {
                horizontal: 'right',
            };
        } catch { }
    };

    const excelOptions = {
        exceljsPreProcess,
        exceljsPostProcess,
        fileName: `${format(new Date(), "yyyy-MM-dd'T'HH:mm")} Profesto - Pay Change`,
    };

    const ExcelExportVariantsMenuItem = (props: GridExportMenuItemProps<{}>) => {
        const apiRef = useGridApiContext();
        const { hideMenu } = props;

        const handleExport = (options: GridExcelExportOptions) => apiRef.current.exportDataAsExcel(options);

        return (
            <>
                <MenuItem onClick={() => { handleExport({ ...excelOptions, allColumns: false }); hideMenu?.(); }}>
                    Visible Columns
                </MenuItem>
                <MenuItem onClick={() => { handleExport({ ...excelOptions, allColumns: true }); hideMenu?.(); }}>
                    All Columns
                </MenuItem>
            </>
        );
    };

    const CustomExportButton = ({ ...other }) => (
        <GridToolbarExportContainer
            sx={{ "&:focus": { backgroundColor: "#DCEEE5", color: '#396 !important', circle: { fill: '#396' } } }} {...other}
        >
            <ExcelExportVariantsMenuItem />
        </GridToolbarExportContainer>
    );

    const buttonStyle = (value: string, type?: string) => {
        if (type === 'bg') {
            if (openedPanelValue === value) {
                return '#DCEEE5';
            }
            if (isFilterSelect && value !== 'columns') {
                return '#F2F2F4';
            }
            return 'transparent';
        } else {
            if (openedPanelValue === value) {
                return '#339966';
            }
        }
    };

    return (
        <GridToolbarContainer {...rest}>
            <DatePicker
                selected={date}
                onChange={setDate}
            />
            <GridToolbarQuickFilter
                sx={quickFilterStyle}
                placeholder='Search...'
                variant="outlined"
                size="small"
                debounceMs={500}
            />
            <div style={{ display: 'flex', marginLeft: 'auto', gap: 20 }}>
                <FilterButtonContainer $isFilterSelect={isFilterSelect || openedPanelValue === 'filters'}>
                    {isFilterSelect ? <StyledCloseIcon onClick={() => apiRef.current.upsertFilterItems([])} /> : null}
                    <GridToolbarFilterButton
                        ref={setPanelsButtonEl}
                        sx={{
                            paddingRight: isFilterSelect ? "30px !important" : '16px',
                            backgroundColor: buttonStyle('filters', 'bg'),
                            color: isFilterSelect || openedPanelValue === 'filters' ? "#339966 !important" : '#364852',
                        }}
                    />
                </FilterButtonContainer>
                <GridToolbarColumnsButton
                    sx={{
                        backgroundColor: buttonStyle('columns', 'bg'),
                        circle: { fill: buttonStyle('columns') },
                        color: openedPanelValue === 'columns' ? '#396 !important' : '#364852',
                    }}
                />
                <CustomExportButton />
            </div>
        </GridToolbarContainer >
    )
};

export default function PayChangeReport() {
    const [openedPanelValue, setOpenedPanelValue] = useState<string | undefined>('');
    const [panelsButtonEl, setPanelsButtonEl] = useState<HTMLButtonElement | null>(null);
    const [numberOfEmployees, setNumberOfEmployees] = useState<number>(0);
    const [date, setDate] = useState<any>(new Date());

    const { data: payChangeReportData = [], isLoading } = useQuery<any>(["pay_change_report", date], {
        endpoint: 'reports/pay_change',
        options: {
            body: { report_date: date },
            method: "post"
        },
    }, { enabled: true });

    const initialState: GridInitialState = {
        sorting: {
            sortModel: [
                { field: 'employee', sort: 'asc' },
            ],
        },
        filter: {
            filterModel: {
                items: [{ id: 1, columnField: 'status', operatorValue: 'isAnyOf', value: ['Hiring', 'Active'] }],
                linkOperator: GridLinkOperator.And,
                quickFilterLogicOperator: GridLinkOperator.And,
            },
        },
        columns: {
            columnVisibilityModel: {
                hire_date: false,
                termination_date: false,
                prev_currency: false,
                prev_amount: false,
                prev_payment_period: false,
                prev_payment_type: false,
                prev_payment_schedule: false,
                prev_overtime: false,
                current_currency: false,
                current_amount: false,
                current_payment_period: false,
                current_payment_type: false,
                current_payment_schedule: false,
                change_reason: false,
            }
        }
    };

    const onGridStateChange = ({ filter: { filteredRowsLookup }, rows: { idRowsLookup } }: GridState) => {
        if (!isEmpty(filteredRowsLookup)) {
            const filteredRowsIds = Object.keys(filteredRowsLookup).filter(key => filteredRowsLookup[key]);
            let rows = Object.keys(idRowsLookup).map((key) => filteredRowsIds.includes(key) && idRowsLookup[key]).filter((row => row));
            let numberOfEmployees = uniqBy(rows, 'employee_id').length;
            setNumberOfEmployees(numberOfEmployees)
        } else {
            let numberOfEmployees = uniqBy(payChangeReportData, 'employee_id').length;
            setNumberOfEmployees(numberOfEmployees)
        }
    };

    return (
        <>
            <PageHeaderTitle title={<HeaderTitle>Pay Change <span>({numberOfEmployees} Employees)</span></HeaderTitle>} />
            <div style={{ display: 'flex', height: '85%', margin: '20px 50px', flexDirection: 'column' }}>
                <DataGrid
                    name="pay_change_report"
                    saveGridState
                    disableRowGrouping
                    onStateChange={onGridStateChange}
                    loading={isLoading}
                    rows={payChangeReportData}
                    columns={columns}
                    initialState={initialState}
                    onPreferencePanelClose={() => setOpenedPanelValue('')}
                    onPreferencePanelOpen={(e: GridPreferencePanelParams) => setOpenedPanelValue(e.openedPanelValue)}
                    components={{
                        Toolbar: CustomToolbar,
                        QuickFilterClearIcon: CloseIcon,
                        QuickFilterIcon: MagnifierIcon
                    }}
                    componentsProps={{
                        panel: {
                            anchorEl: panelsButtonEl,
                            placement: 'bottom'
                        },
                        toolbar: {
                            setPanelsButtonEl,
                            date,
                            setDate,
                            openedPanelValue
                        },
                    }}
                />
            </div>
        </>
    )
};

const HeaderTitle = styled.p`
  font-size: 18px;
  color: #00101A;
  & > span {
    color: #00101A;
    opacity: 0.5;
    font-size: 15px;
  }
`;

const StyledCloseIcon = styled(CloseIcon)`
    position: relative;
    z-index: 2;
    right: -100px;
    top: 1px;
    width: 8px;
    height: 8px;
    cursor: pointer;
    & path {
        fill: #868686;
    }
`;

const FilterButtonContainer = styled.div<{ $isFilterSelect: boolean }>`
    ${StyledCloseIcon}:hover + .MuiButtonBase-root{
        background-color: #DCEEE5;
    }
    & circle { 
        fill: ${({ $isFilterSelect }) => $isFilterSelect ? '#339966' : '#b5b5b5'}
    }
`;

const quickFilterStyle = {
    padding: 0,
    width: 440,
    marginLeft: 1,
    '& input': { padding: '10px 5px' },
    '.MuiButtonBase-root': {
        padding: '5px !important',
        '&:hover': {
            backgroundColor: 'transparent !important',
            'path': { fill: '#636363 !important' }
        },
    },
};
